<template>
  <Spacer />
  <footer
    class="footer"
    aria-labelledby="footer-heading"
    :class="{
      hidden: $route.path.startsWith('/explore'),
    }"
  >
    <div class="footer-container">
      <div class="footer-content">
        <!-- Brand Section -->
        <div class="footer-brand">
          <h2 class="brand-name">Travvir</h2>
          <div class="app-downloads">
            <a
              href="https://play.google.com/store/apps/details?id=com.travvir.studio"
              target="_blank"
              rel="noopener noreferrer"
              class="download-badge"
            >
              <NuxtImg
                format="avif"
                :src="`${cdnUrl}/static/images/PlayStore.png`"
                alt="Get it on Google Play"
                loading="lazy"
              />
            </a>
            <a
              href="https://apps.apple.com/in/app/travvir-360-views-from-phone/id6443536819"
              target="_blank"
              rel="noopener noreferrer"
              class="download-badge"
            >
              <NuxtImg
                format="avif"
                :src="`${cdnUrl}/static/images/AppStore.png`"
                alt="Download on the App Store"
                loading="lazy"
              />
            </a>
          </div>
        </div>

        <!-- Links Sections -->
        <div class="footer-links">
          <div class="first-column">
            <div class="link-column">
              <h3 class="column-title">{{ $t("footer.products.title") }}</h3>
              <ul class="link-list">
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/#virtual-tour`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.products.spatialReplica") }}</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/#reels`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.products.spatialMedia") }}</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/#room-layouts`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.products.spatialLayouts") }}</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/#mira`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.products.miraAi") }}</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/panorama`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.products.panorama") }}</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/virtual-tour`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.products.virtualTour") }}</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/360camera`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.products.360Camera") }}</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/google-streetview`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.products.googleStreetView") }}</NuxtLink
                  >
                </li>
              </ul>
            </div>

            <div class="link-column">
              <h3 class="column-title">{{ $t("footer.company.title") }}</h3>
              <ul class="link-list">
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/about`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.company.aboutUs") }}</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/#join-us`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.company.careers") }}</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/#clients`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.company.clients") }}</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/partnership`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.company.partnership") }}</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink to="/" prefetch-on="visibility">{{
                    $t("footer.company.blog")
                  }}</NuxtLink>
                </li>
              </ul>
            </div>
          </div>
          <div class="second-column">
            <div class="link-column">
              <h3 class="column-title">{{ $t("footer.platform.title") }}</h3>
              <ul class="link-list">
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/enterprise`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.platform.enterprise") }}</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/docs`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.platform.docs") }}</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/explore`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.platform.explore") }}
                  </NuxtLink>
                </li>
                <li>
                  <NuxtLink
                    :to="`${mainSiteUrl}/app/auth/login`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.platform.login") }}
                  </NuxtLink>
                </li>
                <!-- <li v-if="country !== 'IN'">
                  <NuxtLink
                    :to="`${mainSiteUrl}/pricing`"
                    external
                    prefetch-on="visibility"
                    >{{ $t("footer.platform.pricing") }}</NuxtLink
                  >
                </li> -->
              </ul>
            </div>

            <div class="link-column">
              <h3 class="column-title">{{ $t("footer.social.title") }}</h3>
              <ul class="link-list">
                <li>
                  <a
                    target="_blank"
                    href="https://x.com/Travvir_AI"
                    rel="noopener noreferrer"
                    >X</a
                  >
                </li>
                <li>
                  <a
                    target="_blank"
                    href="https://www.linkedin.com/company/travvir/"
                    rel="noopener noreferrer"
                    >LinkedIn</a
                  >
                </li>
              </ul>
            </div>
          </div>
          <div class="link-column content-column">
            <h3 class="column-title">{{ $t("footer.contact.title") }}</h3>
            <ul class="link-list">
              <li v-if="false">
                <p target="_blank" rel="noopener noreferrer">
                  <b>{{ $t("footer.contact.sales") }} </b>
                  <span>+1 (650) 334-0997</span>
                </p>
              </li>
              <li>
                <p target="_blank" rel="noopener noreferrer">
                  <!-- <b>{{ $t("footer.contact.support") }} </b> -->
                  <a href="mailto:operations@travvir.com"
                    >operations@travvir.com</a
                  >
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Bottom Section -->
      <div class="footer-bottom">
        <p class="copyright">
          {{ $t("footer.copyright", { year: new Date().getFullYear() }) }}
        </p>
        <div class="footer-bottom-links">
          <NuxtLink :to="`${mainSiteUrl}/credits`" external>{{
            $t("footer.credits")
          }}</NuxtLink>
          <NuxtLink :to="`${mainSiteUrl}/privacy-policy`" external>{{
            $t("footer.privacyPolicy")
          }}</NuxtLink>
          <NuxtLink :to="`${mainSiteUrl}/terms`" external>{{
            $t("footer.termsOfService")
          }}</NuxtLink>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
const config = useRuntimeConfig();
const mainSiteUrl = config.public.baseUrl;
const cdnUrl = config.public.cdnUrl;
const country = useState("country");
</script>

<style lang="scss" scoped>
.content-column {
  margin-left: -2rem;
  @include respond-below(lg) {
    margin-left: 0rem;
  }
  @include respond-below(md) {
    margin-top: 0;
    margin-left: 0rem;
  }
}
.footer {
  background: #000;
  font-family: $theme-font;
  padding: 4rem 2rem;

  @include respond-below(md) {
    padding: 2rem 1.5rem 0.5rem;
  }

  &.hidden {
    display: none;
  }

  .footer-container {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 2rem;
    padding-right: 0rem;

    @include respond-below(md) {
      padding: 0 1rem;
    }
  }

  .footer-content {
    display: flex;
    justify-content: space-between;
    gap: 4rem;
    padding-bottom: 3rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);

    @include respond-below(lg) {
      display: flex;
      flex-direction: column;
      gap: 3rem;
    }

    @include respond-below(md) {
      gap: 3rem;
      padding-bottom: 2.5rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    }
  }

  .footer-brand {
    .brand-name {
      font-size: 4rem;
      font-weight: $fw-bold;
      font-family: $font-primary;
      color: #fff;
      margin-bottom: 1rem;
      letter-spacing: -0.02em;

      @include respond-below(md) {
        font-size: 2.5rem;
        margin-bottom: 0rem;
        letter-spacing: -0.01em;
      }
    }

    .brand-tagline {
      font-size: $text-sm;
      line-height: 1.6;
      color: rgba(255, 255, 255, 0.5);
      margin-bottom: 2rem;
      max-width: 500px;

      @include respond-below(md) {
        font-size: $text-xs;
      }
    }

    .app-downloads {
      display: flex;
      gap: 1rem;

      .download-badge {
        display: block;
        width: 140px;
        transition: opacity 0.3s ease;

        &:hover {
          opacity: 0.8;
        }

        img {
          width: 100%;
          height: auto;
        }
      }
      @include respond-below(md) {
        display: none;
      }
    }
  }

  .footer-links {
    max-width: 1100px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2.5rem;

    @include respond-below(md) {
      grid-template-columns: repeat(2, 1fr);
      gap: 2.5rem 1.5rem;
      row-gap: 2.5rem;
    }

    @include respond-below(sm) {
      grid-template-columns: 1fr;
      gap: 2.5rem;
    }
    .first-column {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 3rem;

      @include respond-below(md) {
        grid-column: 1 / -1;
        gap: 2.5rem;
      }
    }
    .second-column {
      padding-left: 0rem;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 2rem;
      @include respond-below(md) {
        grid-column: 1 / -1;
        margin-top: 0;
        gap: 2.5rem;
      }
    }

    .link-column {
      @include respond-below(md) {
        min-width: 0;
      }

      .column-title {
        font-size: $text-xs;
        font-weight: $fw-semibold;
        letter-spacing: 0.1em;
        color: rgba(255, 255, 255, 0.5);
        margin-bottom: 1.25rem;
        @include respond-below(md) {
          font-size: 0.75rem;
          letter-spacing: 0.08em;
          margin-bottom: 1rem;
          color: rgba(255, 255, 255, 0.6);
          font-weight: 600;
        }
      }

      .link-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;

        @include respond-below(md) {
          gap: 0.85rem;
        }

        li {
          a,
          p {
            text-wrap: nowrap;
            font-size: $text-sm;
            color: rgba(255, 255, 255, 0.7);
            text-decoration: none;
            transition: color 0.3s ease;

            &:hover {
              color: #fff;
            }

            @include respond-below(md) {
              font-size: 1rem;
              font-weight: 400;
              letter-spacing: 0.01em;
              color: rgba(255, 255, 255, 0.8);
              line-height: 1.3;
            }
          }

          p {
            margin: 0;
            @include respond-below(md) {
              white-space: normal;
              word-break: break-word;

              b {
                font-weight: 600;
                color: rgba(255, 255, 255, 0.9);
              }

              span {
                display: inline;
              }
            }
          }
        }
      }
    }
  }

  .footer-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 2rem;

    @include respond-below(md) {
      flex-direction: column;
      gap: 1.25rem;
      text-align: center;
      padding-top: 1.75rem;
    }

    .copyright {
      font-size: $text-xs;
      color: rgba(255, 255, 255, 0.4);
      margin: 0;

      @include respond-below(md) {
        font-size: 0.8rem;
        color: rgba(255, 255, 255, 0.5);
        order: 2;
      }
    }

    .footer-bottom-links {
      display: flex;
      gap: 1.5rem;

      @include respond-below(md) {
        gap: 1.25rem;
        order: 1;
        flex-wrap: wrap;
        justify-content: center;
      }

      a {
        font-size: $text-xs;
        color: rgba(255, 255, 255, 0.4);
        text-decoration: none;
        transition: color 0.3s ease;

        &:hover {
          color: rgba(255, 255, 255, 0.7);
        }

        @include respond-below(md) {
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.5);
        }
      }
    }
  }
}
</style>
